import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import {
  ZfBox,
  ZfDialog,
  ZfDialogContent,
  ZfDialogFooter,
  ZfDialogHeader,
  ZfIcon,
  ZfInput,
  ZfLink,
  ZfLoadingSpinner,
  ZfPill,
  ZfStack,
  ZfTextButton,
  ZfTypography,
} from '@zafire/react';
import { format, parse, isValid } from 'date-fns';
import { themeZafire } from '@zafire/ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, FilterModel, ICellRendererParams } from 'ag-grid-enterprise';
import { useGetClientRepresentative } from 'hooks/useGetClientRepresentative';
import { useGetBRLinkedClientRepresentatives } from 'hooks/useGetBRLinkedClientRepresentatives';
import { ClientRepItem } from 'types/LegalPartner';
import { relatedPartnerItem } from 'utils/noteDialogUtil';
import { Box } from '@mui/material';
import { EnvConfig } from 'config/envConfig';

type BRClientRepresentativeDialogType = {
  list: relatedPartnerItem[];
  listDetails: ClientRepItem[];
  showDialog: boolean;
  onDialogClose: () => void;
  onProceed: (selectedClientReps: relatedPartnerItem[]) => void;
  isFederated: boolean;
  brUId: string;
  partnerUId: string;
};

export interface ClientRepRow extends ClientRepItem {
  linked: string;
}

const BRClientRepresentativeDialog = ({
  list = [],
  listDetails = [],
  showDialog,
  onDialogClose,
  onProceed,
  isFederated,
  brUId,
  partnerUId,
}: BRClientRepresentativeDialogType) => {
  const { t } = useTranslation();
  const gridRef = useRef<AgGridReact>(null);
  const inputRef = useRef<any>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedLabel, setDebouncedLabel] = useState('');
  const [selectedClientRepList, setSelectedClientRepList] = useState<relatedPartnerItem[]>(list);
  const [filterValue, setFilterValue] = useState<FilterModel | null>(null);

  const [selectedClientRepObjectList, setSelectedClientRepObjectList] = useState<ClientRepItem[]>(listDetails);

  const params = useParams<{ techId: string }>();
  const techId = isFederated ? brUId || partnerUId : params.techId;

  useEffect(() => {
    if (showDialog) {
      setSearchInput('');
      setDebouncedLabel('');
      setSelectedClientRepList(list);
      const filterObjectList = listDetails.filter((item) => list.some((i) => i.value === item.uuid));
      setSelectedClientRepObjectList(filterObjectList);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      if (gridRef.current) {
        gridRef?.current?.api?.setFilterModel(null);
      }
    } else {
      gridRef?.current?.api?.deselectAll();
    }
  }, [showDialog]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
    }),
    [],
  );

  const columnDefs = useMemo<ColDef<ClientRepRow>[]>(() => {
    return [
      {
        field: 'bizKey',
        headerName: 'Partner ID',
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <ZfLink
              size="small"
              type="inline"
              target="_blank"
              rel="noreferrer"
              key={params.data.uuid}
              href={`${EnvConfig.config.hostUrl}/partner/edit/${params.data.uuid}`}
            >
              {params.data.bizKey}
            </ZfLink>
          );
        },
      },
      { field: 'name', headerName: 'Name', flex: 1 },
      {
        field: 'dateOfOrigin',
        headerName: t('contactNote.dialog.brClientRepresentative.dobHeader'),
        flex: 1,
        valueFormatter: (params) => {
          if (params.value) {
            const parsedDate = parse(params.value, 'yyyyMMdd', new Date());
            if (isValid(parsedDate)) {
              return format(parsedDate, 'dd.MM.yyyy');
            }
          }
          return '-';
        },
      },
      {
        field: 'type',
        headerName: t('contactNote.dialog.brClientRepresentative.type'),
        filter: 'agSetColumnFilter',
        flex: 1,
        filterParams: {
          valueFormatter: (params: any) => {
            if (params.value) {
              const label = params.value.toUpperCase();
              const displayLabel = t(`contactNote.dialog.brClientRepresentative.types.${label}`, label) as string;
              return displayLabel.charAt(0) + displayLabel.slice(1)?.toLowerCase();
            }
            return '-';
          },
        },
        cellRenderer: (params: ICellRendererParams<ClientRepRow>) => {
          if (params.value) {
            const label = params.value.toUpperCase();
            return (
              <ZfStack direction="row" style={{ alignItems: 'center', height: '100%' }}>
                <ZfTypography variant="body-small" transform="capitalize">
                  {String(t(`contactNote.dialog.brClientRepresentative.types.${label}`, label))?.toLowerCase()}
                </ZfTypography>
              </ZfStack>
            );
          }
          return '-';
        },
      },
      {
        field: 'linked',
        filter: 'agSetColumnFilter',
        headerName: t('contactNote.dialog.brClientRepresentative.brLinkedHeader'),
        width: 100,
        cellRenderer: (params: ICellRendererParams<ClientRepRow>) => {
          if (params.value === t('contactNote.dialog.brClientRepresentative.yes')) {
            return (
              <ZfStack direction="row" spacing="medium" style={{ alignItems: 'center', height: '100%' }}>
                {' '}
                <ZfIcon name="check" size="medium" />{' '}
                <ZfTypography variant="body-small">
                  {t('contactNote.dialog.brClientRepresentative.yes')}
                </ZfTypography>{' '}
              </ZfStack>
            );
          } else {
            return (
              <ZfStack direction="row" spacing="medium" style={{ alignItems: 'center', height: '100%' }}>
                {' '}
                <ZfIcon name="close" size="medium" />{' '}
                <ZfTypography variant="body-small">
                  {t('contactNote.dialog.brClientRepresentative.no')}
                </ZfTypography>{' '}
              </ZfStack>
            );
          }
        },
      },
    ];
  }, [selectedClientRepList]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLabel(searchInput);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const {
    data: clientRepDetails,
    isFetching: clientRepDetailsLoading,
    isSuccess: clientRepDetailsSuccess,
  } = useGetClientRepresentative(debouncedLabel, debouncedLabel === searchInput);

  const { data: linkedClientRepDetails, isFetching: linkedClientRepDetailsLoading } =
    useGetBRLinkedClientRepresentatives(techId || '', true);

  const clientRepList = useMemo(() => {
    if (clientRepDetailsSuccess && !!clientRepDetails) {
      return clientRepDetails.hits;
    }
    return [];
  }, [clientRepDetails, clientRepDetailsSuccess]);

  const linkedClientRepList = useMemo(() => {
    if (debouncedLabel.length === 0) {
      if (!!linkedClientRepDetails && linkedClientRepDetails.length > 0) {
        const nonLinkedClientRepList =
          selectedClientRepObjectList.length > 0
            ? selectedClientRepObjectList.filter(
                (item) => !linkedClientRepDetails.some((rep) => rep.uuid === item.uuid),
              )
            : [];
        return linkedClientRepDetails
          .map((rep) => ({
            ...rep,
            selected: selectedClientRepList.some((item) => item.value === rep.uuid),
            linked: t('contactNote.dialog.brClientRepresentative.yes'),
          }))
          .concat(
            nonLinkedClientRepList.map((item) => ({
              ...item,
              selected: selectedClientRepList.some((i) => i.value === item.uuid),
              linked: t('contactNote.dialog.brClientRepresentative.no'),
            })),
          );
      } else if (selectedClientRepObjectList.length > 0) {
        return selectedClientRepObjectList.map((item) => ({
          ...item,
          selected: selectedClientRepList.some((i) => i.value === item.uuid),
          linked: t('contactNote.dialog.brClientRepresentative.no'),
        }));
      }
      return [];
    } else {
      if (!!linkedClientRepDetails && linkedClientRepDetails.length > 0 && clientRepList.length > 0) {
        return clientRepList.map((clientRep) => {
          const linkedRep = linkedClientRepDetails.find((linkedRep) => linkedRep.uuid === clientRep.uuid);
          return {
            ...clientRep,
            selected: selectedClientRepList.some((item) => item.value === clientRep.uuid),
            linked: linkedRep
              ? t('contactNote.dialog.brClientRepresentative.yes')
              : t('contactNote.dialog.brClientRepresentative.no'),
          };
        });
      }
      if (clientRepList.length > 0) {
        return clientRepList.map((clientRep) => ({
          ...clientRep,
          selected: selectedClientRepList.some((item) => item.value === clientRep.uuid),
          linked: t('contactNote.dialog.brClientRepresentative.no'),
        }));
      }
      return [];
    }
  }, [debouncedLabel, linkedClientRepDetails, clientRepList, selectedClientRepList]);

  useEffect(() => {
    if (!gridRef.current || linkedClientRepList.length === 0) return;

    gridRef.current?.api?.forEachNode((node) => {
      if (node.data?.selected) {
        node.setSelected(true);
      } else if (node.isSelected() && !node.data?.selected) {
        node.setSelected(false);
      }
    });
  }, [gridRef, linkedClientRepList, showDialog]);

  const onFilterChanged = () => {
    const model = gridRef.current?.api.getFilterModel() ?? null;
    setFilterValue(model);
  };

  const onFilterDelete = (name: string) => {
    const filter = filterValue;
    delete filter?.[name];
    setFilterValue(filter);
    gridRef?.current?.api.setFilterModel(filter);
  };

  return (
    <ZfDialog show={showDialog} size="large" onDialogClose={onDialogClose} disableLightDismiss>
      <ZfDialogHeader>{t('contactNote.dialog.brClientRepresentative.title')}</ZfDialogHeader>
      <ZfDialogContent>
        <ZfStack spacing="medium">
          <ZfStack direction="row" style={{ flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
            <ZfInput
              ref={inputRef}
              style={{ maxWidth: '400px', minWidth: '300px' }}
              type="search"
              placeholder={t('contactNote.dialog.brClientRepresentative.searchPlaceholder')}
              onInputChange={(e) => {
                if (e.detail !== searchInput) {
                  setSearchInput(e.detail);
                }
              }}
              clearable
              onSearchSubmit={(e) => {
                if (e.detail === '') {
                  setSearchInput(e.detail);
                }
              }}
            >
              {(clientRepDetailsLoading || debouncedLabel !== searchInput) && (
                <ZfLoadingSpinner slot="trailing" size="extra-small" color="primary" type="infinite"></ZfLoadingSpinner>
              )}
            </ZfInput>
            <ZfStack direction="row" spacing="small">
              <ZfTypography>{t('contactNote.dialog.brClientRepresentative.activeFilters')}</ZfTypography>
              {filterValue?.type && (
                <ZfPill
                  size="medium"
                  key="filter_type"
                  deletable
                  onPillDelete={() => {
                    onFilterDelete('type');
                  }}
                >
                  {t('contactNote.dialog.brClientRepresentative.type')}
                </ZfPill>
              )}
              {filterValue?.linked && (
                <ZfPill
                  size="medium"
                  key="filter_linked"
                  deletable
                  onPillDelete={() => {
                    onFilterDelete('linked');
                  }}
                >
                  {t('contactNote.dialog.brClientRepresentative.brLinkedHeader')}
                </ZfPill>
              )}
            </ZfStack>
          </ZfStack>
          {selectedClientRepList.length > 0 && (
            <ZfStack direction="row" spacing="small">
              <ZfTypography>{t('contactNote.dialog.brClientRepresentative.selectedPartners')}</ZfTypography>

              <ZfStack direction="row" spacing="small" style={{ flexWrap: 'wrap', flex: 1 }}>
                {selectedClientRepList.length > 0 &&
                  selectedClientRepList.slice(0, 4).map((item) => (
                    <ZfPill
                      size="medium"
                      key={item.value}
                      deletable
                      onPillDelete={() => {
                        const list = selectedClientRepList.filter((i) => i.value !== item.value);
                        setSelectedClientRepList(list);
                        setSelectedClientRepObjectList((prev: ClientRepItem[]) =>
                          prev.filter((object) => object.uuid !== item.value),
                        );
                        gridRef.current?.api.forEachNode((node) => {
                          if (node.data.uuid === item.value) {
                            node.setSelected(false);
                          }
                        });
                      }}
                    >
                      {`${selectedClientRepObjectList.find((i) => i.uuid === item.value)?.name || ''} (${selectedClientRepObjectList.find((i) => i.uuid === item.value)?.bizKey || item.value})`}
                    </ZfPill>
                  ))}
                {selectedClientRepList.length > 4 && (
                  <ZfPill
                    size="medium"
                    clickable={false}
                    key="more_selected_client_rep"
                  >{`+${selectedClientRepList.length - 4}`}</ZfPill>
                )}
              </ZfStack>
            </ZfStack>
          )}
          <Box
            sx={{
              '.ag-row': {
                cursor: 'pointer',
              },
              '.ag-selection-checkbox': {
                pointerEvents: 'none',
              },
            }}
          >
            <ZfBox styles={{ maxHeight: '500px', overflowY: 'auto' }}>
              <ZfBox styles={{ width: '100%' }}>
                <AgGridReact<any>
                  ref={gridRef}
                  domLayout="autoHeight"
                  loading={debouncedLabel ? clientRepDetailsLoading : linkedClientRepDetailsLoading}
                  getRowId={(params) => `${params.data.uuid}`}
                  columnDefs={columnDefs}
                  theme={themeZafire}
                  pagination={true}
                  paginationPageSize={10}
                  onCellClicked={(e) => {
                    if (e.column.getColId() === 'bizKey') {
                      e.event?.stopPropagation();
                    } else {
                      e.node.setSelected(!e.node.isSelected());
                    }
                  }}
                  paginationPageSizeSelector={[10, 20, 50]}
                  rowData={linkedClientRepList}
                  onFirstDataRendered={(params) => {
                    params.api.forEachNode((node) => {
                      if (node.data?.selected) {
                        node.setSelected(true);
                      }
                    });
                  }}
                  onFilterChanged={() => {
                    onFilterChanged();
                  }}
                  onSelectionChanged={(event) => {
                    const selectedRows = event.api.getSelectedRows();
                    if (debouncedLabel.length === 0) {
                      setSelectedClientRepList(selectedRows.map((r) => ({ value: r.uuid, type: r.type })));
                      setSelectedClientRepObjectList(selectedRows);
                      return;
                    } else {
                      const previousSelectedList = linkedClientRepList.filter((item) => item.selected);
                      const currentSelectedList = selectedClientRepList.filter((item) => {
                        if (selectedRows.length === 0) {
                          //clear all selection case that is within the searched result, we want to keep the ones that are not in the searched result but previously selected, and remove the ones that are in the searched result
                          return !previousSelectedList.some((row) => row.uuid === item.value);
                        } else {
                          if (
                            previousSelectedList.length === 0 ||
                            !linkedClientRepList.some((row) => row.uuid === item.value)
                          ) {
                            //keep all previously selected data as there is no previously selected data in the searched result
                            return true;
                          }
                          //normal case, keep the ones that are selected, remove the ones that are not selected
                          return (
                            previousSelectedList.some((row) => row.uuid === item.value) &&
                            selectedRows.some((r) => r.uuid === item.value)
                          );
                        }
                      });
                      const newSelectedList = selectedRows
                        .filter((row) => !currentSelectedList.some((item) => item.value === row.uuid))
                        .map((r) => ({ value: r.uuid, type: r.type }));
                      const newSelectedObjects = selectedRows.filter(
                        (row) => !currentSelectedList.some((item) => item.value === row.uuid),
                      );
                      setSelectedClientRepList([...currentSelectedList, ...newSelectedList]);
                      setSelectedClientRepObjectList((prev) => [
                        ...prev.filter((item) => currentSelectedList.some((i) => i.value === item.uuid)),
                        ...newSelectedObjects,
                      ]);
                    }
                  }}
                  suppressCellFocus
                  rowSelection={{ mode: 'multiRow', enableClickSelection: false }}
                  defaultColDef={defaultColDef}
                />
              </ZfBox>
            </ZfBox>
          </Box>
        </ZfStack>
      </ZfDialogContent>
      <ZfDialogFooter>
        <ZfStack slot="primary" direction="row" spacing="small">
          <ZfTextButton size="medium" hierarchy="tertiary" onClick={onDialogClose}>
            {t('contactNote.dialog.brClientRepresentative.cancel')}
          </ZfTextButton>
          <ZfTextButton
            size="medium"
            onClick={() => {
              onProceed(selectedClientRepList);
            }}
          >
            {t('contactNote.dialog.brClientRepresentative.add')}
          </ZfTextButton>
        </ZfStack>
      </ZfDialogFooter>
    </ZfDialog>
  );
};

export default BRClientRepresentativeDialog;
